import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../axiosInstance';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f0f4f8;
  padding: 2rem;
  font-family: 'Roboto', sans-serif;
`;

const FormContainer = styled(motion.div)`
  max-width: 600px;
  width: 100%;
  padding: 2rem;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(28, 47, 79, 0.1);
`;

const FormTitle = styled.h2`
  margin-bottom: 1.5rem;
  font-size: 2rem;
  color: #1c2f4f;
  text-align: center;
  font-weight: bold;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  margin-bottom: 1rem;
  border: 1px solid #e6eef8;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #1c2f4f;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  margin-bottom: 1rem;
  border: 1px solid #e6eef8;
  border-radius: 8px;
  font-size: 1rem;
  resize: vertical;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #1c2f4f;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
`;

const Button = styled(motion.button)`
  background-color: #1c2f4f;
  color: #fff;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: bold;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #152238;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  color: #dc3545;
  margin-bottom: 1rem;
  text-align: center;
`;

const SessionInfo = styled.p`
  margin-bottom: 1rem;
  font-weight: bold;
  color: #1c2f4f;
  text-align: center;
`;

const LoadingOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(28, 47, 79, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LoadingSpinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #1c2f4f;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(28, 47, 79, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background-color: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(28, 47, 79, 0.1);
  text-align: center;
  max-width: 400px;
  width: 100%;
`;

const ModalTitle = styled.h2`
  color: #1c2f4f;
  margin-bottom: 1rem;
  font-weight: bold;
`;

const ModalText = styled.p`
  margin-bottom: 1.5rem;
  color: #1c2f4f;
`;

const ModalButton = styled(Button)`
  width: 100%;
`;

const BookingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tutorName, session } = location.state || {};

  const [studentName, setStudentName] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    console.log('Session data:', session); // Debug log
  }, [session]);

  if (!tutorName || !session) {
    return <PageContainer><FormContainer><ErrorMessage>Invalid booking details. Please go back and try again.</ErrorMessage></FormContainer></PageContainer>;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsLoading(true);

    try {
      const response = await axiosInstance.post('/api/availability/book-session', { 
        tutorName, 
        session, 
        studentName, 
        email, 
        topic 
      });
      
      if (response.status === 200) {
        setShowSuccessModal(true);
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (error) {
      console.error('Error booking session:', error.response?.data || error.message);
      setErrorMessage(error.response?.data?.message || 'Failed to book session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatSessionDateTime = () => {
    if (!session.date || !session.startTime || !session.endTime) {
      return 'Date and time information unavailable';
    }
    return `${session.date}, ${session.startTime} - ${session.endTime}`;
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    navigate('/');
  };

  return (
    <PageContainer>
      <FormContainer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {isLoading && (
          <LoadingOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <LoadingSpinner />
          </LoadingOverlay>
        )}
        {showSuccessModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ModalContent
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
            >
              <ModalTitle>Booking Successful!</ModalTitle>
              <ModalText>
                Your session with {tutorName} has been booked successfully for {formatSessionDateTime()}.
              </ModalText>
              <ModalButton onClick={handleCloseModal}>
                Close
              </ModalButton>
            </ModalContent>
          </ModalOverlay>
        )}
        <FormTitle>Book Session with {tutorName}</FormTitle>
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        <form onSubmit={handleSubmit}>
          <Input
            type="text"
            placeholder="Your Name"
            value={studentName}
            onChange={(e) => setStudentName(e.target.value)}
            required
            disabled={isLoading}
          />
          <Input
            type="email"
            placeholder="Your Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
          />
          <Textarea
            placeholder="Please describe the topic you need help with"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            required
            disabled={isLoading}
            rows="4"
          />
          <SessionInfo>Session Time: {formatSessionDateTime()}</SessionInfo>
          <ButtonContainer>
            <Button
              type="submit"
              disabled={isLoading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isLoading ? 'Booking...' : 'Book Session'}
            </Button>
            <Button
              type="button"
              onClick={() => navigate('/')}
              disabled={isLoading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Cancel
            </Button>
          </ButtonContainer>
        </form>
      </FormContainer>
    </PageContainer>
  );
};

export default BookingPage;